/**
 * TranscriptNextSteps (KAN-665): "Что делать дальше" in the header of the
 * transcript modal.
 *
 * The block shows the action plan of the lead card (the same card the manager
 * gets by e-mail, GET /clients/{id}/lead-card/): numbered steps, what was
 * promised to the client and the notes (what is missing). Collapsible; the
 * open/closed state is remembered per browser. While loading a short spinner
 * is shown; on an error the block is not rendered at all.
 *
 * KAN-686: the client's messages written after the hand-off to a manager (the
 * bot is silent then) are listed under "Новое от клиента после передачи".
 *
 * KAN-687: the heading is the card's action.title ("Что сделать менеджеру" for
 * the steps built from the whole conversation). Those steps may be counted by
 * a model while the request runs (up to ~20 s), so a load longer than usual
 * shows "Собираем шаги...".
 *
 * KAN-690: "Отправить на почту" next to the heading sends the current card to
 * e-mail (POST /clients/{id}/lead-card/send/); disabled with a spinner while
 * the request runs, the result is a toast.
 */
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Mail } from 'lucide-react';
import { loadJson, saveJson } from '@/lib/persistedState';
import { useCanCallApi } from '@/hooks/useApiAccess';
import {
  useClientLeadCard,
  useSendLeadCard,
} from '@/services/api/clientQueries';
import { ANY_UUID } from '@/lib/managerAccess';

const OPEN_STORAGE_KEY = 'transcript-next-steps-open';
const DEFAULT_TITLE = 'Что делать дальше';
/** After this long a load is "longer than usual": the steps are being counted. */
const SLOW_LOAD_MS = 1500;

/** True once ``active`` has stayed true for ``delayMs``. */
function useSlow(active: boolean, delayMs: number): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!active) {
      setSlow(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setSlow(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [active, delayMs]);
  return slow;
}

interface TranscriptNextStepsProps {
  clientId: string;
  enabled: boolean;
}

export function TranscriptNextSteps({
  clientId,
  enabled,
}: TranscriptNextStepsProps): JSX.Element | null {
  const { data, isLoading, isError } = useClientLeadCard(clientId, enabled);
  const slow = useSlow(isLoading, SLOW_LOAD_MS);
  const sendCard = useSendLeadCard(clientId);
  // KAN-811: sending the card by e-mail is closed to the MANAGER role.
  const canSendCard = useCanCallApi('POST', `/api/v1/clients/${clientId ?? ANY_UUID}/lead-card/send/`);
  // Open by default; a remembered "false" closes it.
  const [open, setOpen] = useState(() => loadJson(OPEN_STORAGE_KEY) !== false);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    saveJson(OPEN_STORAGE_KEY, next);
  };

  if (isError) return null;
  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 text-xs text-gray-500"
        data-testid="transcript-next-steps-loading"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        {slow ? 'Собираем шаги...' : 'Загрузка рекомендаций…'}
      </div>
    );
  }
  if (!data) return null;

  const steps = data.action.steps;
  const afterHandoff = data.after_handoff ?? [];
  if (
    steps.length === 0 &&
    !data.promised &&
    !data.notes &&
    afterHandoff.length === 0
  ) {
    return null;
  }

  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <section
      className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs"
      data-testid="transcript-next-steps"
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-1 font-medium text-gray-900"
          aria-expanded={open}
          onClick={toggle}
          data-testid="transcript-next-steps-toggle"
        >
          <Chevron className="h-3 w-3" />
          {data.action.title || DEFAULT_TITLE}
        </button>
        {canSendCard && (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => sendCard.mutate()}
            disabled={sendCard.isPending}
            data-testid="transcript-next-steps-send"
          >
            {sendCard.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Mail className="h-3 w-3" />
            )}
            Отправить на почту
          </button>
        )}
      </div>
      {open && (
        <div
          className="mt-1 max-h-40 space-y-1 overflow-y-auto text-gray-700"
          data-testid="transcript-next-steps-body"
        >
          {steps.length > 0 && (
            <ol className="list-decimal space-y-0.5 pl-5">
              {steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          )}
          {afterHandoff.length > 0 && (
            <div data-testid="transcript-after-handoff">
              <p className="font-medium text-gray-900">
                Новое от клиента после передачи
              </p>
              <ul className="space-y-0.5">
                {afterHandoff.map((message, index) => (
                  <li key={index}>
                    <span className="text-gray-500">{message.stamp}</span>{' '}
                    {message.text}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.promised && (
            <p>
              <span className="text-gray-500">Обещано клиенту:</span>{' '}
              {data.promised}
            </p>
          )}
          {data.notes && (
            <p>
              <span className="text-gray-500">Примечания:</span> {data.notes}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
